/// <amd-module name="Artifacts/Pypi/RestClient/PyPiApi" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   packaging\client\pypi\webapi\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as PyPiApi from "Artifacts/Pypi/Contracts/PyPiApi";
import * as PackagingShared from "Artifacts/Root/types/PackagingShared";

import { IPyPiApiRestClient } from "Artifacts/Pypi/RestClient/PyPiApi.Types";

export { IPyPiApiRestClient } from "Artifacts/Pypi/RestClient/PyPiApi.Types";

class PyPiApiRestClient extends RestClientBase implements IPyPiApiRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Download a python package file directly. This API is intended for manual UI download options, not for programmatic access and scripting.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param fileName - Name of the file in the package
     * @param project - Project ID or project name
     */
    public async downloadPackage(
        feedId: string,
        packageName: string,
        packageVersion: string,
        fileName: string,
        project?: string
        ): Promise<ArrayBuffer> {

        return this.beginRequest<ArrayBuffer>({
            apiVersion: "6.1-preview.1",
            httpResponseType: "application/octet-stream",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/packages/{packageName}/versions/{packageVersion}/{fileName}/content",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion,
                fileName: fileName
            }
        });
    }

    /**
     * Update several packages from a single feed in a single request. The updates to the packages do not happen atomically.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data.
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async updatePackageVersions(
        batchRequest: PyPiApi.PyPiPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/packagesBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Delete or restore several package versions from the recycle bin.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data. \<c\>Operation\</c\> must be \<c\>PermanentDelete\</c\> or \<c\>RestoreToFeed\</c\>
     * @param feedId - Feed which contains the packages to update.
     * @param project - Project ID or project name
     */
    public async updateRecycleBinPackageVersions(
        batchRequest: PyPiApi.PyPiPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/RecycleBin/packagesBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Delete a package version from the feed, moving it to the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async deletePackageVersionFromRecycleBin(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Get information about a package version in the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getPackageVersionMetadataFromRecycleBin(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<PyPiApi.PyPiPackageVersionDeletionState> {

        return this.beginRequest<PyPiApi.PyPiPackageVersionDeletionState>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Restore a package version from the recycle bin to its associated feed.
     * 
     * @param packageVersionDetails - Set the 'Deleted' state to 'false' to restore the package to its feed.
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async restorePackageVersionFromRecycleBin(
        packageVersionDetails: PyPiApi.PyPiRecycleBinPackageVersionDetails,
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

    /**
     * Get the upstreaming behavior of a package within the context of a feed
     * 
     * @param feedId - The name or id of the feed
     * @param packageName - The name of the package
     * @param project - Project ID or project name
     */
    public async getUpstreamingBehavior(
        feedId: string,
        packageName: string,
        project?: string
        ): Promise<PackagingShared.UpstreamingBehavior> {

        return this.beginRequest<PackagingShared.UpstreamingBehavior>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/packages/{packageName}/upstreaming",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName
            }
        });
    }

    /**
     * Set the upstreaming behavior of a package within the context of a feed
     * 
     * @param feedId - The name or id of the feed
     * @param packageName - The name of the package
     * @param behavior - The behavior to apply to the package within the scope of the feed
     * @param project - Project ID or project name
     */
    public async setUpstreamingBehavior(
        feedId: string,
        packageName: string,
        behavior: PackagingShared.UpstreamingBehavior,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/packages/{packageName}/upstreaming",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName
            },
            body: behavior
        });
    }

    /**
     * Delete a package version, moving it to the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async deletePackageVersion(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<PyPiApi.Package> {

        return this.beginRequest<PyPiApi.Package>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Get information about a package version.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     * @param showDeleted - True to show information for deleted package versions.
     */
    public async getPackageVersion(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string,
        showDeleted?: boolean
        ): Promise<PyPiApi.Package> {

        const queryValues: any = {
            showDeleted: showDeleted
        };

        return this.beginRequest<PyPiApi.Package>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            queryParams: queryValues
        });
    }

    /**
     * Update state for a package version.
     * 
     * @param packageVersionDetails - Details to be updated.
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async updatePackageVersion(
        packageVersionDetails: PyPiApi.PackageVersionDetails,
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/pypi/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

}

export const PyPiApiClientName = "PyPiApi-AzureArtifacts.IPyPiApiRestClient";

export function getPyPiApiClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IPyPiApiRestClient {
    return pageContext.getRestClient<IPyPiApiRestClient>(PyPiApiClientName, options);
}

RestClients.add(PyPiApiClientName, { 
    factory: PyPiApiRestClient,
    options: {
        resourceAreaId: "92f0314b-06c5-46e0-abe7-15fd9d13276a",
        serviceInstanceType: "00000030-0000-8888-8000-000000000000"
    }
});
